/**
 * CP932文字カテゴリバリデーター
 * cp932_categories_list.csvに基づいて文字の妥当性を検証
 */

import * as iconv from "iconv-lite";

export interface CP932Category {
  category: string;
  englishKey: string;
  startCode: number;
  endCode: number;
}

export interface ValidationOptions {
  halfWidthSymbol1?: boolean;
  halfWidthDigits?: boolean;
  halfWidthSymbol2?: boolean;
  halfWidthUppercaseLetters?: boolean;
  halfWidthSymbol3?: boolean;
  halfWidthLowercaseLetters?: boolean;
  halfWidthSymbol4?: boolean;
  halfWidthKatakana?: boolean;
  symbols?: boolean;
  fullWidthDigits?: boolean;
  fullWidthUppercaseLetters?: boolean;
  fullWidthLowercaseLetters?: boolean;
  hiragana?: boolean;
  katakana?: boolean;
  greekUppercase?: boolean;
  greekLowercase?: boolean;
  russianUppercase?: boolean;
  russianLowercase?: boolean;
  boxDrawing?: boolean;
  platformDependent1?: boolean;
  kanjiLevel1?: boolean;
  kanjiLevel2Part1?: boolean;
  kanjiLevel2Part2?: boolean;
  platformDependent2?: boolean;
  platformDependent3?: boolean;
}

export interface ValidationResult {
  invalidCharacters: string[];
}

export const HALF_WIDTH_CHARACTER_OPTION: ValidationOptions = {
  halfWidthDigits: true,
  halfWidthUppercaseLetters: true,
  halfWidthLowercaseLetters: true,
  halfWidthKatakana: true,
  halfWidthSymbol1: true,
  halfWidthSymbol4: true,
};

export const FULL_WIDTH_CHARACTER_OPTION: ValidationOptions = {
  fullWidthDigits: true,
  fullWidthUppercaseLetters: true,
  fullWidthLowercaseLetters: true,
};

export const JAPANESE_CHARACTER_OPTION: ValidationOptions = {
  symbols: true,
  hiragana: true,
  katakana: true,
  kanjiLevel1: true,
  kanjiLevel2Part1: true,
  kanjiLevel2Part2: true,
};

/**
 * CP932カテゴリ定義（cp932_categories_list.csvから）
 */
const CP932_CATEGORIES: CP932Category[] = [
  { category: "半角記号1", englishKey: "halfWidthSymbol1", startCode: 0x20, endCode: 0x2f }, // 半角スペース～/
  { category: "半角数字", englishKey: "halfWidthDigits", startCode: 0x30, endCode: 0x39 }, // 0～9
  { category: "半角記号2", englishKey: "halfWidthSymbol2", startCode: 0x3a, endCode: 0x40 }, // :～@
  { category: "半角英字(大)", englishKey: "halfWidthUppercaseLetters", startCode: 0x41, endCode: 0x5a }, // A～Z
  { category: "半角記号3", englishKey: "halfWidthSymbol3", startCode: 0x5b, endCode: 0x60 }, // [～`
  { category: "半角英字(小)", englishKey: "halfWidthLowercaseLetters", startCode: 0x61, endCode: 0x7a }, // a～z
  { category: "半角記号4", englishKey: "halfWidthSymbol4", startCode: 0x7b, endCode: 0x7e }, // {～~
  { category: "半角カタカナ", englishKey: "halfWidthKatakana", startCode: 0xa1, endCode: 0xdf }, // ｡～ ﾟ
  { category: "記号類", englishKey: "symbols", startCode: 0x8140, endCode: 0x81ff }, // ＿～◯など
  { category: "全角数字", englishKey: "fullWidthDigits", startCode: 0x824f, endCode: 0x8258 }, // ０～９
  { category: "全角英字(大)", englishKey: "fullWidthUppercaseLetters", startCode: 0x8260, endCode: 0x8279 }, // Ａ～Ｚ
  { category: "全角英字(小)", englishKey: "fullWidthLowercaseLetters", startCode: 0x8281, endCode: 0x829a }, // ａ～ｚ
  { category: "ひらがな", englishKey: "hiragana", startCode: 0x829f, endCode: 0x82f1 }, // ぁ～ん
  { category: "カタカナ", englishKey: "katakana", startCode: 0x8340, endCode: 0x8396 }, // ァ～ヶ
  { category: "ギリシャ文字(大)", englishKey: "greekUppercase", startCode: 0x839f, endCode: 0x83b6 }, // Α～Ω
  { category: "ギリシャ文字(小)", englishKey: "greekLowercase", startCode: 0x83bf, endCode: 0x83d6 }, // α～ω
  { category: "ロシア文字(大)", englishKey: "russianUppercase", startCode: 0x8440, endCode: 0x8460 }, // А～Я
  { category: "ロシア文字(小)", englishKey: "russianLowercase", startCode: 0x8470, endCode: 0x8491 }, // а～я
  { category: "罫線", englishKey: "boxDrawing", startCode: 0x849f, endCode: 0x84be }, // ─～╂
  { category: "機種依存1", englishKey: "platformDependent1", startCode: 0x8740, endCode: 0x879f }, // ①や㈱など
  { category: "漢字第一水準", englishKey: "kanjiLevel1", startCode: 0x889f, endCode: 0x9872 }, // 亜唖娃など
  { category: "漢字第二水準1", englishKey: "kanjiLevel2Part1", startCode: 0x989f, endCode: 0x9ffc }, // 弌丐丕などから滯漲滌などまで
  { category: "漢字第二水準2", englishKey: "kanjiLevel2Part2", startCode: 0xe040, endCode: 0xeaa4 }, // 漾漓滷などから瑤凜熙などまで
  { category: "機種依存2", englishKey: "platformDependent2", startCode: 0xed40, endCode: 0xeeec }, // 纊褜鍈などの漢字第三水準以上を含む
  { category: "機種依存3", englishKey: "platformDependent3", startCode: 0xfa40, endCode: 0xfc4b }, // 小文字のローマ数字、纊褜鍈などの漢字第三水準以上を含む
];

/**
 * 文字をCP932でエンコードしてバイト配列を取得
 */
function encodeToCP932(char: string): number[] | null {
  try {
    const buffer = iconv.encode(char, "cp932");
    if (!buffer || buffer.length === 0) {
      return null;
    }
    return Array.from(buffer);
  } catch (error) {
    return null;
  }
}

/**
 * CP932バイトコードがカテゴリ範囲内かチェック
 */
function isInCP932Range(bytes: number[], category: CP932Category): boolean {
  let code: number;
  if (bytes.length === 1) {
    code = bytes[0];
  } else if (bytes.length === 2) {
    code = (bytes[0] << 8) | bytes[1];
  } else {
    return false;
  }

  return code >= category.startCode && code <= category.endCode;
}

/**
 * 文字が指定されたカテゴリに属するかチェック
 */
function isCharacterInCategory(char: string, category: CP932Category): boolean {
  const cp932Bytes = encodeToCP932(char);
  if (!cp932Bytes) {
    return false;
  }

  return isInCP932Range(cp932Bytes, category);
}

/**
 * 文字が有効なカテゴリに属するかチェック
 */
function isCharacterValid(char: string, enabledEnglishKeys: Set<string>): boolean {
  for (const category of CP932_CATEGORIES) {
    if (enabledEnglishKeys.has(category.englishKey) && isCharacterInCategory(char, category)) {
      return true;
    }
  }
  return false;
}

/**
 * CP932カテゴリに基づいて文字列を検証
 * @param text 検証する文字列
 * @param options カテゴリの有効/無効設定（指定されなかったカテゴリは無効）
 * @returns 無効な文字の配列
 */

const BLOCKED_CHARACTERS = new Set(["ヸ", "ゕ", "ゖ"]);

const ALLOWED_CHARACTERS = new Set(["[", "@", "^", "_", "=", "[", "]", ";", ":", "<", ">", "?", "]"]);

export function validateCP932Characters(text: string, options: ValidationOptions = {}): string[] {
  const enabledEnglishKeys = new Set<string>();

  for (const [englishKey, enabled] of Object.entries(options)) {
    if (enabled) {
      enabledEnglishKeys.add(englishKey);
    }
  }

  const invalidCharacters: string[] = [];

  for (const char of text) {
    if (ALLOWED_CHARACTERS.has(char)) {
      continue;
    }
    if (BLOCKED_CHARACTERS.has(char)) {
      if (!invalidCharacters.includes(char)) {
        invalidCharacters.push(char);
      }
      continue;
    }
    if (!isCharacterValid(char, enabledEnglishKeys)) {
      if (!invalidCharacters.includes(char)) {
        invalidCharacters.push(char);
      }
    }
  }

  return invalidCharacters;
}

/**
 * 利用可能なカテゴリ一覧を取得
 */
export function getAvailableCategories(): string[] {
  return CP932_CATEGORIES.map((cat) => cat.category);
}
